import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { EmailViewModel } from '../../../models/Entities/Person/Email/EmailModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PersonEmailFormComponent from './PersonEmail.Form.Component';

// functions
import * as ComponentFunctions from './PersonEmails.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const PersonEmailsListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedEmailId, setSelectedEmailId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (props.PersonId) props.GetEmailsByPersonId(props.PersonId);
  }, [props.PersonId]);

  const handleOpenCreate = () => {
    setSelectedEmailId(undefined);
    openForm();
  };

  const handleEdit = (emailId: number) => {
    setSelectedEmailId(emailId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedEmailId(undefined);
    dismissForm();
  };

  return (
    <>
      <div className="ComponentHeaderControls">
        <PrimaryButton
          text={t('persons.emails.list.add', { defaultValue: 'Agregar correo' })}
          iconProps={AddIcon}
          onClick={handleOpenCreate}
          className="actionButton"
        />
      </div>
      <Separator />

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.emails.list.error', { defaultValue: 'No fue posible obtener los correos' })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && props.emails.length === 0 && (
        <MessageComponent
          message={t('persons.emails.list.empty', { defaultValue: 'No hay correos registrados' })}
          type={MessageBarType.info}
        />
      )}

      {props.emails.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.emails.list.email', { defaultValue: 'Correo electrónico' })}</th>
              <th>{t('persons.emails.list.comments', { defaultValue: 'Comentarios' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.emails.map((email: EmailViewModel) => (
              <tr key={email.id} className="TableBodyRow">
                <td className="primary">
                  <Label>{email.email || '-'}</Label>
                </td>
                <td>
                  <Text>{email.comments || '-'}</Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(email.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`PersonEmailDeleteButton-${email.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.emails.list.deleteTitle', { defaultValue: 'Eliminar correo' })}
                      Description={t('persons.emails.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar el correo seleccionado?'
                      })}
                      ElementTextHeader={t('persons.emails.list.email', { defaultValue: 'Correo electrónico' })}
                      ElementTextInformation={email.email || ''}
                      onConfirmation={() => props.DeleteEmail(email.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.emails.list.loading', { defaultValue: 'Cargando correos...' })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <PersonEmailFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        PersonId={props.PersonId}
        emailId={selectedEmailId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PersonEmailsListComponent as any);
